import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  ArrowLeft,
  Share2,
  Heart,
  Video,
  Globe,
  Building2,
  User,
  Mail,
  Phone,
  Plus,
  Edit2,
  Trash2,
  Ticket,
  X,
  Layout,
} from "lucide-react";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../../lib/usePerformRequest";
import { Endpoints } from "../../../../lib/endpoints";
import {
  IEventProps,
  IEventTicketProps,
  IEventTicketDetailsProps,
  NonPaginatedResponse,
} from "../../../../types";
import { Loader } from "../../../../components/ui/loader";
import { EmptyState } from "../../../../components/ui/empty-state";
import { ImageWithFallback } from "../../../../components/figma/ImageWithFallback";
import { Currencies } from "../../../../lib/data/currencies";
import { useToastNotification } from "../../../../hooks/useToast";
import { HttpCodes } from "../../../../lib/data";
import {
  Button,
  Center,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Stack,
  Group,
  Text,
  Title,
  Paper,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import "./styles.scss";

const SingleEvent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eventId = id ? parseInt(id, 10) : null;
  const toast = useToastNotification();

  // Ticket management state
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<IEventTicketProps | null>(
    null
  );
  const [deletingTicket, setDeletingTicket] =
    useState<IEventTicketProps | null>(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [isDeletingTicket, setIsDeletingTicket] = useState(false);
  const [isLoadingTicketDetails, setIsLoadingTicketDetails] = useState(false);
  const [tickets, setTickets] = useState<IEventTicketProps[]>([]);

  // Currency options
  const currencyOptions = useMemo(() => {
    return Currencies.map((currency) => ({
      value: currency.code,
      label: `${currency.code} - ${currency.name}`,
    }));
  }, []);

  const eventEndpoint = useMemo(() => {
    if (!eventId) return null;
    return Endpoints.GetSingleEvent(eventId);
  }, [eventId]);

  const {
    data: eventResponse,
    isLoading,
    reloadData,
  } = usePerformRequest<{
    event: IEventProps;
    ticket_sales: IEventTicketProps[];
  }>({
    method: eventEndpoint?.method as any,
    route: eventEndpoint?.route || "",
  });

  const event = useMemo(() => {
    if (!eventResponse) return null;
    if (Array.isArray(eventResponse)) return null;
    return eventResponse?.event ?? null;
  }, [eventResponse]);

  const ticketSales = useMemo(() => {
    if (!eventResponse) return [];
    if (Array.isArray(eventResponse)) return [];
    return eventResponse?.ticket_sales ?? [];
  }, [eventResponse]);

  // Update tickets state when ticketSales changes
  useEffect(() => {
    setTickets(ticketSales);
  }, [ticketSales]);

  const { totalSeats, isPaidEvent } = useMemo(() => {
    if (!tickets || tickets.length === 0) {
      return {
        totalSeats: 0,
        isPaidEvent: false,
      };
    }

    const seatsFromTickets = tickets.reduce((acc, ticket) => {
      const quantity =
        typeof ticket.quantity === "number"
          ? ticket.quantity
          : parseInt(String(ticket.quantity ?? 0), 10);
      return acc + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);

    const hasPaidTicket = tickets.some((ticket) => {
      const price =
        typeof ticket.price === "number"
          ? ticket.price
          : parseFloat(String(ticket.price ?? 0));
      return Number.isFinite(price) && price > 0;
    });

    return {
      totalSeats: seatsFromTickets,
      isPaidEvent: hasPaidTicket,
    };
  }, [tickets]);

  // Ticket form
  const ticketForm = useForm({
    initialValues: {
      ticket_type: "",
      price: "" as number | "",
      currency: "",
      quantity: "" as number | "",
      min_order_limit: 1 as number | "",
      max_order_limit: "" as number | "",
      instructions: "",
    },
    validate: {
      ticket_type: (value) =>
        value.trim().length > 0 ? null : "Ticket type is required",
      price: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "Price is required";
        }
        if (typeof value === "number" && value < 0) {
          return "Price cannot be negative";
        }
        return null;
      },
      currency: (value) => (value ? null : "Currency is required"),
      quantity: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "Quantity is required";
        }
        if (typeof value === "number" && value <= 0) {
          return "Quantity must be greater than 0";
        }
        return null;
      },
      min_order_limit: (value) => {
        if (value === "" || value === null || value === undefined) {
          return "Minimum order limit is required";
        }
        if (typeof value === "number" && value < 1) {
          return "Minimum order limit must be at least 1";
        }
        return null;
      },
      max_order_limit: (value, values) => {
        // Optional field, but if provided, must be >= min_order_limit
        if (value !== "" && value !== null && value !== undefined) {
          const minLimit =
            typeof values.min_order_limit === "number"
              ? values.min_order_limit
              : 1;
          if (typeof value === "number" && value < minLimit) {
            return `Maximum order limit must be at least ${minLimit}`;
          }
        }
        return null;
      },
    },
  });

  const handleOpenAddTicket = () => {
    setEditingTicket(null);
    ticketForm.reset();
    ticketForm.setFieldValue("min_order_limit", 1);
    setTicketModalOpen(true);
  };

  const handleOpenEditTicket = async (ticket: IEventTicketProps) => {
    setEditingTicket(ticket);
    setIsLoadingTicketDetails(true);
    setTicketModalOpen(true);

    try {
      const response = await PerformRequest<
        NonPaginatedResponse<{
          ticket: IEventTicketDetailsProps;
        }>
      >({
        method: Endpoints.GetEventTicketUsingTicketID(ticket.id).method as any,
        route: Endpoints.GetEventTicketUsingTicketID(ticket.id).route,
        onError: () => {
          setIsLoadingTicketDetails(false);
          toast.error("Failed to load ticket details");
        },
      });

      setIsLoadingTicketDetails(false);

      if (
        response.status === HttpCodes.Success &&
        response.data?.data?.ticket
      ) {
        const ticketDetails = response.data.data?.ticket;
        // Get currency code - handle both object and string formats
        const currencyCode =
          typeof ticketDetails.currency === "string"
            ? ticketDetails.currency
            : (ticketDetails.currency as any)?.code || "";

        ticketForm.setValues({
          ticket_type: ticketDetails.ticket_type || "",
          price: parseFloat(ticketDetails.price?.toString() || "0") || "",
          currency: currencyCode,
          quantity: ticketDetails.quantity || "",
          min_order_limit: ticketDetails.min_order_limit || 1,
          max_order_limit: ticketDetails.max_order_limit || "",
          instructions: ticketDetails.instructions || "",
        });
      } else {
        toast.error("Failed to load ticket details");
        setTicketModalOpen(false);
        setEditingTicket(null);
      }
    } catch (error) {
      setIsLoadingTicketDetails(false);
      toast.error("An error occurred while loading ticket details");
      setTicketModalOpen(false);
      setEditingTicket(null);
    }
  };

  const handleOpenDeleteTicket = (ticket: IEventTicketProps) => {
    setDeletingTicket(ticket);
    setDeleteModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    if (!isLoadingTicketDetails) {
      setTicketModalOpen(false);
      setEditingTicket(null);
      setIsLoadingTicketDetails(false);
      ticketForm.reset();
    }
  };

  const handleSubmitTicket = async () => {
    if (!eventId) return;

    const { hasErrors } = ticketForm.validate();
    if (hasErrors) {
      toast.error("Please fix the form errors");
      return;
    }

    setIsSubmittingTicket(true);
    const {
      ticket_type,
      price,
      currency,
      quantity,
      min_order_limit,
      max_order_limit,
      instructions,
    } = ticketForm.values;

    try {
      const endpoint = editingTicket
        ? Endpoints.GetUpdateTicketInEvent(editingTicket.id)
        : Endpoints.GetAddTicketToEvent(eventId);

      // Build payload
      const payload: any = {
        ticket_type: ticket_type.trim(),
        price: typeof price === "number" ? price : parseFloat(price as any),
        currency,
        quantity:
          typeof quantity === "number" ? quantity : parseInt(quantity as any),
        min_order_limit:
          typeof min_order_limit === "number"
            ? min_order_limit
            : parseInt(min_order_limit as any) || 1,
      };

      // Add optional fields if provided
      if (
        max_order_limit !== "" &&
        max_order_limit !== null &&
        max_order_limit !== undefined
      ) {
        payload.max_order_limit =
          typeof max_order_limit === "number"
            ? max_order_limit
            : parseInt(max_order_limit as any);
      }

      if (instructions && instructions.trim().length > 0) {
        payload.instructions = instructions.trim();
      }

      const response = await PerformRequest({
        method: endpoint.method as any,
        route: endpoint.route,
        data: payload,
        onError: () => setIsSubmittingTicket(false),
      });

      setIsSubmittingTicket(false);

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        toast.success(
          editingTicket
            ? "Ticket updated successfully"
            : "Ticket added successfully"
        );
        handleCloseTicketModal();
        reloadData?.();
      } else {
        toast.error(response.message || "Failed to save ticket");
      }
    } catch (error) {
      setIsSubmittingTicket(false);
      toast.error("An error occurred while saving the ticket");
    }
  };

  const handleDeleteTicket = async () => {
    if (!deletingTicket || !eventId) return;

    setIsDeletingTicket(true);

    try {
      const response = await PerformRequest({
        method: Endpoints.GetDeleteTicketFromEvent(deletingTicket.id)
          .method as any,
        route: Endpoints.GetDeleteTicketFromEvent(deletingTicket.id).route,
        onError: () => setIsDeletingTicket(false),
      });

      setIsDeletingTicket(false);

      if (response.status === HttpCodes.Success) {
        toast.success("Ticket deleted successfully");
        setDeleteModalOpen(false);
        setDeletingTicket(null);
        reloadData?.();
      } else {
        toast.error(response.message || "Failed to delete ticket");
      }
    } catch (error) {
      setIsDeletingTicket(false);
      toast.error("An error occurred while deleting the ticket");
    }
  };

  const formatCurrency = (ticket: IEventTicketProps) => {
    if (typeof ticket.currency === "string") {
      return ticket.currency;
    }
    return (ticket.currency as any)?.code || "N/A";
  };

  const formatLocation = () => {
    if (!event) return "";
    if (event.type === "online") return "Online Event";
    if (event.type === "hybrid") return "Hybrid Event";
    if (event.location.address) {
      return `${event.location.address}, ${event.location.city}, ${event.location.state}`;
    }
    return `${event.location.city}, ${event.location.country}`;
  };

  const formatDate = () => {
    if (!event) return "";
    const date = new Date(event.date);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = () => {
    if (!event) return "";
    const date = new Date(event.date);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-page__loader">
          <Loader size="lg" variant="primary" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-page">
        <div className="event-detail-page__empty">
          <EmptyState
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path d="M3 10h18" />
              </svg>
            }
            title="Event not found"
            description="The event you're looking for doesn't exist or has been removed."
            action={
              <Link to="/d/events">
                <button className="event-detail-page__back-button">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Events
                </button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const summaryDetails = [
    {
      icon: Calendar,
      label: "Date",
      value: new Date(event.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      accent: "#2563eb",
    },
    {
      icon: Clock,
      label: "Time",
      value: formatTime(),
      accent: "#7c3aed",
    },
    {
      icon: Users,
      label: "Seats",
      value: `${totalSeats} available`,
      accent: "#0ea5e9",
    },
  ];

  return (
    <div className="event-detail-page">
      {/* Back Button */}
      <div className="event-detail-page__header">
        <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/d/events")}
          className="event-detail-page__back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Events
        </button>
          <Button
            leftSection={<Layout size={18} />}
            onClick={() => navigate(`/d/events/${id}/page-builder/event`)}
            style={{ backgroundColor: "#17265a" }}
          >
            Customize Event Page
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="event-detail-page__hero">
        <div className="event-detail-page__hero-image">
          <ImageWithFallback
            src={event.cover_image || ""}
            alt={event.name}
            className="event-detail-page__cover-image"
          />
          <div className="event-detail-page__hero-overlay" />

          {/* Badges */}
          <div className="event-detail-page__hero-badges">
            <span
              className={`event-detail-page__badge event-detail-page__badge--type event-detail-page__badge--${event.type}`}
            >
              {event.type === "online" && <Globe className="w-4 h-4" />}
              {event.type === "hybrid" && <Video className="w-4 h-4" />}
              {event.type === "offline" && <MapPin className="w-4 h-4" />}
              {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
            </span>
            <span
              className={`event-detail-page__badge event-detail-page__badge--price ${
                isPaidEvent ? "is-paid" : "is-free"
              }`}
            >
              {isPaidEvent ? "Paid Event" : "Free Event"}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="event-detail-page__hero-actions">
            <button className="event-detail-page__action-button">
              <Heart className="w-5 h-5" />
            </button>
            <button className="event-detail-page__action-button">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="event-detail-page__content">
        <div className="event-detail-page__main">
          {/* Title Section */}
          <div className="event-detail-page__title-section">
            <h1 className="event-detail-page__title">{event.name}</h1>
            <div className="event-detail-page__host">
              <User className="w-4 h-4" />
              <span>Hosted by {event.host_name}</span>
            </div>
            {event.organizer_description && (
              <p className="event-detail-page__organizer-desc">
                {event.organizer_description}
              </p>
            )}
          </div>

          {/* Key Info Grid */}
          <div className="event-detail-page__info-grid">
            <InfoCard
              icon={Calendar}
              label="Date & Time"
              value={formatDate()}
              accent="#0f5eea"
            />
            <InfoCard
              icon={MapPin}
              label="Location"
              value={formatLocation()}
              accent="#12b76a"
            />
            <InfoCard
              icon={Users}
              label="Capacity"
              value={`${totalSeats} seats available`}
              accent="#7f56d9"
            />
            <InfoCard
              icon={Clock}
              label="Time"
              value={formatTime()}
              accent="#f2b01d"
            />
          </div>

          {/* Description */}
          <div className="event-detail-page__section">
            <h2 className="event-detail-page__section-title">
              About This Event
            </h2>
            <p className="event-detail-page__description">
              {event.event_description}
            </p>
          </div>

          {/* Ticket Management Section */}
          <div className="event-detail-page__section">
            <div className="event-detail-page__section-header">
              <div>
                <h2 className="event-detail-page__section-title">
                  Ticket Management
                </h2>
                <Text size="sm" c="dimmed" mt={4}>
                  Manage tickets for this event
                </Text>
              </div>
              <Button
                leftSection={<Plus size={18} />}
                onClick={handleOpenAddTicket}
                className="event-detail-page__add-ticket-button"
              >
                Add Ticket
              </Button>
            </div>

            {tickets.length === 0 ? (
              <div className="event-detail-page__tickets-empty">
                <Ticket
                  size={48}
                  className="event-detail-page__tickets-empty-icon"
                />
                <Text size="lg" fw={500} mt="md">
                  No tickets yet
                </Text>
                <Text size="sm" c="dimmed" mt={4}>
                  Get started by adding your first ticket tier
                </Text>
              </div>
            ) : (
              <div className="event-detail-page__tickets-grid">
                {tickets.map((ticket) => (
                  <Paper
                    key={`${ticket.id}-${ticket.ticket_type}`}
                    className="event-detail-page__ticket-card"
                    shadow="sm"
                    p="lg"
                    radius="md"
                    withBorder
                  >
                    <div className="event-detail-page__ticket-card-header">
                      <div className="event-detail-page__ticket-card-icon">
                        <Ticket size={20} />
                      </div>
                      <Group gap="xs">
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => handleOpenEditTicket(ticket)}
                          className="event-detail-page__ticket-action-button"
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          variant="subtle"
                          size="xs"
                          color="red"
                          onClick={() => handleOpenDeleteTicket(ticket)}
                          className="event-detail-page__ticket-action-button"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </Group>
                    </div>
                    <div className="event-detail-page__ticket-card-content">
                      <Text
                        fw={600}
                        size="lg"
                        className="event-detail-page__ticket-card-type"
                      >
                        {ticket.ticket_type}
                      </Text>
                      <div className="event-detail-page__ticket-card-details">
                        <div className="event-detail-page__ticket-card-detail-item">
                          <Text size="xs" c="dimmed">
                            Price
                          </Text>
                          <Text fw={600} size="md">
                            {ticket.price} {formatCurrency(ticket)}
                          </Text>
                        </div>
                        <div className="event-detail-page__ticket-card-detail-item">
                          <Text size="xs" c="dimmed">
                            Quantity
                          </Text>
                          <Text fw={600} size="md">
                            {ticket.quantity} available
                          </Text>
                        </div>
                      </div>
                    </div>
                  </Paper>
                ))}
              </div>
            )}
          </div>

          {/* Video Conference Info */}
          {(event.type === "online" || event.type === "hybrid") &&
            event.location.video_conference_link && (
              <div className="event-detail-page__section">
                <h2 className="event-detail-page__section-title">
                  Video Conference Details
                </h2>
                <div className="event-detail-page__video-info">
                  <div className="event-detail-page__video-item">
                    <span className="event-detail-page__video-label">
                      Platform:
                    </span>
                    <span className="event-detail-page__video-value">
                      {event.location.video_conference_platform}
                    </span>
                  </div>
                  <div className="event-detail-page__video-item">
                    <span className="event-detail-page__video-label">
                      Link:
                    </span>
                    <a
                      href={event.location.video_conference_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-detail-page__video-link"
                    >
                      {event.location.video_conference_link}
                    </a>
                  </div>
                  {event.location.video_conference_id && (
                    <div className="event-detail-page__video-item">
                      <span className="event-detail-page__video-label">
                        Meeting ID:
                      </span>
                      <span className="event-detail-page__video-value">
                        {event.location.video_conference_id}
                      </span>
                    </div>
                  )}
                  {event.location.video_conference_password && (
                    <div className="event-detail-page__video-item">
                      <span className="event-detail-page__video-label">
                        Password:
                      </span>
                      <span className="event-detail-page__video-value">
                        {event.location.video_conference_password}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Location Details for Offline Events */}
          {event.type === "offline" && event.location.address && (
            <div className="event-detail-page__section">
              <h2 className="event-detail-page__section-title">
                Venue Details
              </h2>
              <div className="event-detail-page__venue-info">
                <div className="event-detail-page__venue-item">
                  <MapPin className="w-5 h-5" />
                  <div>
                    <p className="event-detail-page__venue-address">
                      {event.location.address}
                    </p>
                    <p className="event-detail-page__venue-location">
                      {event.location.city}, {event.location.state}{" "}
                      {event.location.postal_code}
                    </p>
                    <p className="event-detail-page__venue-country">
                      {event.location.country}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="event-detail-page__sidebar">
          <div className="event-detail-page__sidebar-card is-summary">
            <div className="event-detail-page__sidebar-header">
              <div>
                <p className="event-detail-page__sidebar-eyebrow">
                  At a glance
                </p>
                <h3>Event Summary</h3>
              </div>
              <span className="event-detail-page__status-pill">
                {event.type === "online"
                  ? "Virtual"
                  : event.type === "hybrid"
                  ? "Hybrid"
                  : "In person"}
              </span>
            </div>
            <div className="event-detail-page__summary-list">
              {summaryDetails.map(({ icon: Icon, label, value, accent }) => (
                <div
                  key={label}
                  className="event-detail-page__summary-item"
                  style={{ "--summary-accent": accent } as React.CSSProperties}
                >
                  <div className="event-detail-page__summary-icon">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="event-detail-page__summary-label">{label}</p>
                    <p className="event-detail-page__summary-value">{value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="event-detail-page__cta">
              <Center>
                <button className="event-detail-page__cta-button">
                  RSVP Now
                </button>
              </Center>
              <p className="event-detail-page__cta-hint">
                Secure your spot before seats run out.
              </p>
            </div>
            {totalSeats && totalSeats < 20 && (
              <div className="event-detail-page__warning">
                <Clock className="w-4 h-4" />
                <span>Only {totalSeats || 0} seats remaining</span>
              </div>
            )}
          </div>

          {/* Host Info */}
          <div className="event-detail-page__sidebar-card is-host">
            <div className="event-detail-page__sidebar-header">
              <h3>Host Information</h3>
            </div>
            <div className="event-detail-page__sidebar-content">
              <div className="event-detail-page__host-info">
                <div className="event-detail-page__host-avatar">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <p className="event-detail-page__host-name">
                    {event?.host_name}
                  </p>
                  <p className="event-detail-page__host-email">
                    {event?.user?.email || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Ticket Modal */}
      <Modal
        opened={ticketModalOpen}
        onClose={handleCloseTicketModal}
        title={
          <div className="event-detail-page__modal-header">
            <Ticket size={24} />
            <Title order={3}>
              {editingTicket ? "Edit Ticket" : "Add New Ticket"}
            </Title>
          </div>
        }
        centered
        size="md"
        className="event-detail-page__ticket-modal"
        closeOnClickOutside={!isLoadingTicketDetails}
        closeOnEscape={!isLoadingTicketDetails}
        withCloseButton={!isLoadingTicketDetails}
      >
        {isLoadingTicketDetails ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader size="lg" variant="primary" />
              <Text size="sm" c="dimmed">
                Loading ticket details...
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="md">
            <TextInput
              label="Ticket Type"
              placeholder="e.g., VIP, Early Bird, General Admission"
              required
              size="md"
              {...ticketForm.getInputProps("ticket_type")}
            />

            <Group grow>
              <div style={{ width: "100%" }}>
                <NumberInput
                  label="Price"
                  placeholder="0.00"
                  required
                  size="md"
                  decimalScale={2}
                  min={0}
                  {...ticketForm.getInputProps("price")}
                />
                {ticketForm.values.price === 0 && (
                  <Text size="xs" c="blue" mt={4}>
                    Setting the price to 0 makes this ticket free for attendees.
                  </Text>
                )}
              </div>

              <Select
                label="Currency"
                placeholder="Select currency"
                required
                size="md"
                data={currencyOptions}
                searchable
                {...ticketForm.getInputProps("currency")}
              />
            </Group>

            <NumberInput
              label="Quantity Available"
              placeholder="0"
              required
              size="md"
              min={1}
              {...ticketForm.getInputProps("quantity")}
            />

            <Group grow>
              <NumberInput
                label="Minimum Order Limit"
                placeholder="1"
                required
                size="md"
                min={1}
                description="Minimum number of tickets a user must buy"
                {...ticketForm.getInputProps("min_order_limit")}
              />

              <NumberInput
                label="Maximum Order Limit"
                placeholder="Optional"
                size="md"
                min={1}
                description="Maximum number of tickets a user can buy"
                {...ticketForm.getInputProps("max_order_limit")}
              />
            </Group>

            <Textarea
              label="Instructions"
              placeholder="e.g., Please arrive 20 minutes before event for registration"
              size="md"
              minRows={3}
              description="Optional instructions for ticket holders"
              {...ticketForm.getInputProps("instructions")}
            />

            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="subtle"
                onClick={handleCloseTicketModal}
                disabled={isSubmittingTicket}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                loading={isSubmittingTicket}
                className="event-detail-page__modal-submit-button"
              >
                {editingTicket ? "Update Ticket" : "Add Ticket"}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingTicket(null);
        }}
        title={
          <div className="event-detail-page__modal-header">
            <Trash2 size={24} color="#dc2626" />
            <Title order={3}>Delete Ticket</Title>
          </div>
        }
        centered
        size="sm"
        className="event-detail-page__delete-modal"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Are you sure you want to delete the ticket{" "}
            <Text component="span" fw={600} c="dark">
              "{deletingTicket?.ticket_type}"
            </Text>
            ? This action cannot be undone.
          </Text>

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeletingTicket(null);
              }}
              disabled={isDeletingTicket}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteTicket}
              loading={isDeletingTicket}
            >
              Delete Ticket
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

interface InfoCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  accent: string;
}

const InfoCard: React.FC<InfoCardProps> = ({
  icon: Icon,
  label,
  value,
  accent,
}) => {
  return (
    <div
      className="event-detail-page__info-card"
      style={{ "--accent": accent } as React.CSSProperties}
    >
      <div className="event-detail-page__info-icon">
        <Icon className="w-5 h-5" />
      </div>
      <div className="event-detail-page__info-content">
        <span className="event-detail-page__info-label">{label}</span>
        <span className="event-detail-page__info-value">{value}</span>
      </div>
    </div>
  );
};

export default SingleEvent;
